import { baseGlobals } from '@root/typescript/baseGlobals'

import { is } from '../is/is'
import { Object } from '../Object/Object'

import type BaseWebAudioSystem from '../BaseWebAudioSoundSystem/BaseWebAudioSoundSystem'

import type { IFadeParams, IPlayParams, IWebAudioAddParams } from './definitions'

const DEFAULT_SEQUENCE_DELAY = 0

type TVolume = Record<string, number>

export default abstract class BaseSoundPlayer {
  protected keys: string[] = []
  protected subfix = ''

  private isEnabled = true
  private savedVolumes: TVolume = {}

  protected abstract prefix: string
  protected abstract soundSystem: BaseWebAudioSystem

  public get enabled (): boolean {
    return this.isEnabled
  }

  public set enabled (isEnabled: boolean) {
    this.isEnabled = isEnabled
  }

  /**
   * Hangok hangerejének beállítása.
   * @param volume   - Hangerő.
   * @param duration - Mennyi idő alatt halkitsa le a zenét?
   */
  public setVolumes (volume: number, duration = 0): void {
    // Ha azonnal szeretnénk lehalkítani.
    if (duration === 0) {
      this.keys.forEach((key) => {
        this.setVolume(key, volume)
      })

      return
    }

    // Ha van duration, akkor fade-elünk.
    this.keys.forEach((key) => {
      this.soundSystem.fade({
        key,
        duration,
        to: volume,
        needToStoreVolume: true
      })
    })
  }

  /**
   * Hangerő beállítása.
   * @param key    - A hangfájl kulcsa.
   * @param volume - Hangerő.
   */
  public setVolume (key: string, volume: number): void {
    const prefixed = this.getKey(key)

    this.soundSystem.setAudioVolume(prefixed, volume)
  }

  /** Hangerők elmentése. */
  public saveVolumes (): void {
    this.savedVolumes = this.keys.reduce((acc: TVolume, curr) => {
      const volume = this.soundSystem.getAudioVolume(curr)

      acc[curr] = volume

      return acc
    }, {})
  }

  /** Hangerők betöltése. */
  public loadVolumes (): void {
    const savedVolumeKeys = Object.getKeys(this.savedVolumes)

    savedVolumeKeys.forEach((key) => {
      const volume = this.savedVolumes[key]

      this.soundSystem.setAudioVolume(key, volume)
    })
  }

  /**
   * Hangfájl hosszának elkérése.
   * @param key - A hangfájl hossza.
   */
  public getAudioDuration (key: string): number | null {
    const prefixed = this.getKey(key)

    return this.soundSystem.getAudioDuration(prefixed)
  }

  /**
   * Component prefixelt kulcs, ami a SoundSystem-nek passzolja tovább.
   * @param params - Params.
   */
  public add (params: IWebAudioAddParams): Promise<void> {
    const prefixed = this.getKey(params.key)

    if (!this.keys.includes(prefixed)) {
      this.keys.push(prefixed)
    }

    return this.soundSystem.add({ ...params, key: prefixed })
  }

  /**
   * Prefixelt hang lejátszás.
   * @param params - Params.
   * @throws  Hibát dobunk, ha nincs ilyen kulcs.
   */
  public play (params: IPlayParams): void | number {
    // ha TEST, vagy kikapcsoltuk az effekteket.
    if (baseGlobals.isTest || !this.enabled) {
      if (params.onEnd) {
        params.onEnd()
      }

      return
    }

    const prefixed = this.getKey(params.key)

    const isValidKey = this.keys.includes(prefixed)

    if (!isValidKey && baseGlobals.isDev) {
      throw new TypeError(`[${ this.prefix }] nincs ilyen kulcs: ${ prefixed }`)
    }

    return this.soundSystem.play({ ...params, key: prefixed })
  }

  /**
   * Szekvencia lejátszása.
   * @param keys    - Lejátszandó hangok kulcsai.
   * @param delay   - Hangok lejátszása közötti szünet.
   * @param isForce - Lelőjjük az aktuálisan futó hangot?
   * @param volume  - Hangerő.
   */
  public playSequence ( // eslint-disable-line @typescript-eslint/max-params -- Legacy.
    keys: string[],
    delay: number = DEFAULT_SEQUENCE_DELAY,
    isForce = false,
    volume?: number
  ): Promise<void> {
    // ha TEST, vagy kikapcsoltuk az effekteket.
    if (baseGlobals.isTest || !this.enabled) {
      return Promise.resolve()
    }

    const prefixedKeys = keys.map((key) => this.getKey(key))

    return this.soundSystem.playSequence(prefixedKeys, delay, isForce, volume)
  }

  /**
   * Egyetlen, prefixelt hangot állít le.
   * @param key - A kulcs, ami alapján leállítjuk a hangfájl lejátszását.
   * @param id  - Megállítani kívánt lejátszás id-ja.
   */
  public stop (key: string, id?: number): void {
    const prefixed = this.getKey(key)
    this.soundSystem.stop(prefixed, id)
  }

  /**
   * Egyetlen, prefixelt hang lejátszását szünetelteti.
   * @param key - A kulcs, ami alapján szüneteltetjük a hangfájl lejátszását.
   * @param id  - Szüneteltetni kívánt lejátszás id-ja.
   */
  public pause (key: string, id?: number): void {
    const prefixed = this.getKey(key)

    this.soundSystem.pause(prefixed, id)
  }

  /**
   * Egyetlen, prefixelt hangnak folytatja a lejátszását.
   * @param key - A kulcs, ami alapján folytatjuk a hangfájl lejátszását.
   * @param id  - Folytatni kívánt lejátszás id-ja.
   */
  public resume (key: string, id?: number): void {
    const prefixed = this.getKey(key)

    this.soundSystem.resume(prefixed, id)
  }

  /**
   * Épp lejátszuk a kulcshoz tartozó hangforrást?
   * @param key - A kulcs, ami alapján vizsgálunk.
   */
  public isPlaying (key: string): boolean {
    const prefixed = this.getKey(key)

    return this.soundSystem.isPlaying(prefixed)
  }

  /** Az összes futó hangot lelövi. */
  public stopAll (): void {
    this.keys.forEach((key) => {
      this.soundSystem.stop(key)
    })
  }

  /**
   * A lejátszott hangot a megadott időintervallumon keresztül lehallkítja, majd megállítja.
   * @param params - Paraméterek.
   */
  public fade (
    params: IFadeParams
  ): void {
    const prefixed = this.getKey(params.key)

    this.soundSystem.fade({ ...params, key: prefixed })
  }

  /** Nincsenek kulcsok az adott Playerhez. */
  public hasNoAudios (): boolean {
    return is.empty(this.keys)
  }

  /**
   * Kulcs generálása.
   * @param key    - Új kulcs.
   * @param prefix - Prefix string.
   * @param subfix - Subfix string.
   */
  protected getKey (key: string, prefix = this.prefix, subfix = this.subfix): string {
    return `${ prefix }${ is.empty(subfix) ? '' : `:${ subfix }` }:${ key }`
  }
}
